import { Collection, HorizontalAlignment, MapSettings, ModelRow, StringUtil, UserSettings, VerticalAlignment } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button, ButtonVariant, Component, DomEvent, Layout, PanelProps } from "../..";
import { ComponentTypes } from "../../base/ComponentTypes";
import { ListenerListDef } from "../../base/ListenerListDef";
import { DataSourceMode } from "../../databinding/DataSource";
import { ClickEvent } from "../../events/ClickEvent";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { Overlay } from "../../page/Overlay";
import { Label } from "../label/Label";
import { Panel } from "../panel/Panel";
import { GoogleMap } from "./GoogleMap";
import { MapPin } from "./MapPin";
import { MapPropDefinitions, MapProps, MapVendor } from "./MapProps";
import { NoMap } from "./NoMap";
import { PinPlotEvent, PinPlotListener } from "./PinPlotEvent";
import { TrimbleMap } from "./TrimbleMap";

export interface VendorMap {
    removeAllPins: () => void;
    addPin: (pin: MapPin) => void;
    focusOnPin: (pin: MapPin, mapPinIndex: number) => void;
    defocusOnPin: (pin: MapPin, mapPinIndex: number) => void;
    fitPins: () => void;
    setZoom: (level: number) => void;
    getZoom: () => number;
    createRoute: (routeData: any[], routetype?: any, hideMarkers?: boolean | false, routeId?: string, routeColor?: string, showProposedRoute?: boolean, disableToggleButtons?: boolean) => void;
    createRouteFromCoordArray: (routeData: any[], routetype?: any, hideMarkers?: boolean | false, removeAllPins?: boolean | true) => void;
    setStopZoomLevel: (isTrafficEnabled: string, routeData: any[]) => void;
    clearRoute: () => void;
    setCenter: (latitude: number, longitude: number) => void;
    getCenter: () => [number, number];
    showCircle: (latitude: number, longitude: number, radius: number) => void;
    removeCircle: () => void;
    isStyleLoaded: () => boolean;
    onStyleLoad: (callback: () => void) => void;
}

const _pinPlotListenerDef: ListenerListDef = {listName: "_pinPlotListeners"};
const _dragEndListenerDef: ListenerListDef = {listName: "_dragEndListeners"};
const log = DesignableObjectLogManager.getLogger("components.Map");

export class Map extends Panel implements MapProps, VendorMap { // not sure if this should implement VendorMap or not
    map: Panel & VendorMap;
    private _clickToActivate: Label;
    private _immediatelyActivateMap: boolean;
    private _vendor: MapVendor;
    pins: MapPin[] = [];
    pinLayout: string;
    popupProps: Partial<PanelProps>;
    private _pinDetailNonModal = false;
    private _pinDetailOverlays: Overlay[] = [];
    _disableDefaultUI: boolean;
    createMapCallback: () => void;

    public latitudeField: string;
    public longitudeField: string;
    public singleRecord: boolean;
    private loadPromise: Promise<void>;

    public get clickToActivate() : Label {
        return this._clickToActivate;
    }

    constructor(props?: Partial<MapProps>) {
        super({padding: 0, borderRadius: 4, ...props});
        //The mapping vendor must be known at this point so that we can avoid creating the wrong
        //VendorMap in cases where the map is shown immediately (when click to activate isn't used).
        //To put it another way, we don't want to create a VendorMap for the default vendor (Google),
        //and then immediately afterward destroy that map and create a map for another vendor (i.e. Trimble).
        //That would be a poor use of an API call against the vendor.
        //As a result, the vendor cannot be changed after the Map is constructed.
        this.vendor = props?.vendor;
        this.disableDefaultUI = props?.disableDefaultUI;
    }


    override syncInnerComponentIds() {
        this.setInnerComponentId(this._clickToActivate, "clickToActivate");
    }

    public async ensureLoaded(): Promise<void> {
        if (this.loadPromise == null) {
            this.loadPromise = this.internalEnsureLoaded();
        }
        await this.loadPromise;
    }

    private async internalEnsureLoaded() {
        const doNotAlwaysShowGoogleMap = this.isDoNotAlwaysShowGoogleMaps();

        if (doNotAlwaysShowGoogleMap) {
            this._createClickToActivate();
        } else {
            await this._createMap();
        }
    }

    public async ensureLocationMapLoaded(alwaysShowMap: string): Promise<void> {
        if (this.loadPromise == null) {
            this.loadPromise = this.locationInternalEnsureLoaded(alwaysShowMap);
        }
        await this.loadPromise;
    }

    private async locationInternalEnsureLoaded(alwaysShowMap: string) {
        const isNotAlwaysShowMapsAndGoogle = this.isDoNotAlwaysShowGoogleMaps(alwaysShowMap);

        if (isNotAlwaysShowMapsAndGoogle) {
            this._createClickToActivate();
        } else {
            await this._createMap();
        }
    }

    private async _createMap() {
        if (this.map != null || this._designer != null)
            return;

        this.remove(this._clickToActivate);
        this._clickToActivate = null;

        const vendor = this.resolveVendor();

        if (vendor == null || vendor === MapVendor.GOOGLE) {
            this.map = new GoogleMap({ id: "map", parentMap: this, fillRow: true, fillHeight: true, disableDefaultUI: this.disableDefaultUI});
            //this is gross, but we have to wait on the google map script to load
            //(and I couldn't figure out how to make the GoogleMap constructor async/await)
            await this.map.ensureLoaded();
        }
        else if (vendor == MapVendor.TRIMBLE) {
            this.map = new TrimbleMap({ id: "map", parentMap: this, fillRow: true, fillHeight: true, disableDefaultUI: this.disableDefaultUI });
            await this.map.ensureLoaded();
        } else if (vendor == MapVendor.NONE) {
            this.map = new NoMap({id: "map", fillRow: true, fillHeight: true});
            // await (this.map as NoMap);
        } else
            throw new Error("Unknown map vendor " + vendor);
        this.add(this.map);
        this.dataSource?.displayDataInBoundComponent(this);

        if (this.createMapCallback) {
            this.createMapCallback();
        }
    }

    public get vendor(): MapVendor {
        return this._vendor || this.getPropertyDefinitions().vendor.defaultValue;
    }

    public set vendor(value: MapVendor) {
        this._vendor = value;
    }

    private resolveVendor(): MapVendor {
        if (this._vendor === MapVendor.DEFAULT_DISTANCE_VENDOR)
            return this.getDefaultDistanceVendor();
        else if (this._vendor === MapVendor.DEFAULT_FINDNEAR_VENDOR)
            return this.getDefaultFindNearVendor();
        else if (this._vendor === MapVendor.DEFAULT_SINGLE_POINT_VENDOR)
            return this.getDefaultSinglePointVendor();
        else if (this._vendor === MapVendor.DEFAULT_MOVEMENT_MAP_VENDOR)
            return this.getDefaultMovementMapVendor();
        else if (this._vendor === MapVendor.DEFAULT_REQUEST_LOCATION_MAP_VENDOR)
            return this.getDefaultRequestLocationMapVendor();
        else if (this._vendor === MapVendor.DEFAULT_VIEW_MOVEMENT_MAP_VENDOR)
            return this.getDefaultViewMovementMapVendor();
        else if (this._vendor === MapVendor.DEFAULT_MC_POSITION_VENDOR)
            return this.getDefaultMobileCommPositionVendor();
        else if (this._vendor === MapVendor.DEFAULT_PARTIAL_SHIPMENT_VENDOR)
            return this.getDefaultPartialShipmentVendor();
        else if (this._vendor === MapVendor.DEFAULT_ZONE_MAP_VENDOR)
            return this.getDefaultZoneMapVendor();
        return this.vendor;
    }

    private getDefaultDistanceVendor(): MapVendor {
        return this.getMapVendorPerSetting(MapSettings.get()?.distance_calc_vendor);
    }

    private getDefaultFindNearVendor(): MapVendor {
        return this.getMapVendorPerSetting(MapSettings.get()?.findnear_vendor);
    }

    private getDefaultSinglePointVendor(): MapVendor {
        return this.getMapVendorPerSetting(MapSettings.get()?.single_point_vendor);
    }

    private getDefaultMovementMapVendor(): MapVendor {
        return this.getMapVendorPerSetting(MapSettings.get()?.movement_map_vendor);
    }

    private getDefaultRequestLocationMapVendor(): MapVendor {
        return this.getMapVendorPerSetting(MapSettings.get()?.request_location_vendor);
    }

    private getDefaultViewMovementMapVendor(): MapVendor {
        return this.getMapVendorPerSetting(MapSettings.get()?.view_movement_vendor);
    }

    private getDefaultMobileCommPositionVendor(): MapVendor {
        const userMcPositionVendor= UserSettings.get()?.mc_position_vendor;
        if (userMcPositionVendor) {
            return this.getMapVendorPerSetting(userMcPositionVendor);
        } else {
            return this.getMapVendorPerSetting(MapSettings.get()?.mc_position_vendor);
        }
    }

    private getDefaultPartialShipmentVendor(): MapVendor {
        return this.getMapVendorPerSetting(MapSettings.get()?.partial_shipment_vendor);
    }

    getDefaultZoneMapVendor(): MapVendor {
        return this.getMapVendorPerSetting(MapSettings.get()?.zone_map);
    }

    private getMapVendorPerSetting(mapVendor: string): MapVendor {
        switch (mapVendor) {
            case 'G':
                return MapVendor.GOOGLE;
            case 'W':
                return MapVendor.TRIMBLE;
            case 'N':
                return MapVendor.NONE;
            default:
                return null;
        }

    }

    public get immediatelyActivateMap(): boolean {
        return this._immediatelyActivateMap || this.getPropertyDefinitions().immediatelyActivateMap.defaultValue;
    }

    public set immediatelyActivateMap(value: boolean) {
        if (value === this._immediatelyActivateMap)
            return;
        this._immediatelyActivateMap = value;
        if (value === true && this.contains(this._clickToActivate))
            this._createMap();
    }

    public get disableDefaultUI(): boolean {
        return this._disableDefaultUI || this.getPropertyDefinitions().disableDefaultUI.defaultValue;
    }

    public set disableDefaultUI(value: boolean) {
        this._disableDefaultUI = value;
    }

    private _createClickToActivate() {
        this.remove(this._clickToActivate);
        const inSearchMode = this.dataSource?.mode === DataSourceMode.SEARCH;
        this._clickToActivate = new Label({
            id: this.createInnerComponentId("clickToActivate"),
            fillRow: true,
            fillHeight: true,
            imageProps: {
                name: ImageName.map,
                height: 32,
                width: 32,
                color: "primary",
                align: HorizontalAlignment.LEFT
            },
            align: HorizontalAlignment.CENTER,
            verticalAlign: VerticalAlignment.CENTER,
            borderWidth: 1,
            borderRadius: 4,
            borderColor: "strokeSecondary",
            fontSize: "xxxlarge",
            fontBold: true,
            text: inSearchMode !== true ? "Click to activate map" : "Map not available while searching",
            color: "primary",
            onClick: inSearchMode !== true ? () => this._createMap() : null
        });
        this.add(this._clickToActivate);
    }

    override dataSourceModeChanged(mode: DataSourceMode): void {
        if (this._clickToActivate != null)
            this._createClickToActivate();
    }

    override displayComponentData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        if (this._clickToActivate != null)
            return;
        this.ensureLoaded().then(() => {
            this.removeAllPins();
            if (data != null && this.latitudeField != null && this.longitudeField != null) {
                const displayArray = this.singleRecord === true ? [data] : allData;
                for (const disp of displayArray) {
                    const lat = disp.get(this.latitudeField);
                    const lng = disp.get(this.longitudeField);
                    if (lat != null && lng != null) {
                        const pin = new MapPin(lat, lng);
                        pin.data = disp;
                        this.addPin(pin);
                    }
                }
                this.fitPins();
            }
        });
    }

    private async getConfiguredMovementMapVendor(): Promise<MapVendor> {
        let vendor: MapVendor = null;
        try {
            const vendorString = MapSettings.getSingleton().getMovementMapVendor();
            vendor = this.getMapVendorPerSetting(vendorString);
        } catch (error) {
            log.error(error);
        }
        return vendor;
    }

    override _serializeNonProps(): string {
        return "";
    }

    addPin(pin: MapPin) {
        if (this.map == null)
            return;
        this.pins.push(pin);
        this.fireListeners(_pinPlotListenerDef, () => new PinPlotEvent(this, pin));
        this.map.addPin(pin);
    }

    focusOnPin(pin: MapPin, mapPinIndex: number) {
        this.map?.focusOnPin(pin, mapPinIndex);
    }

    defocusOnPin(pin: MapPin, mapPinIndex: number) {
        this.map?.defocusOnPin(pin, mapPinIndex);
    }

    removeAllPins() {
        if (this.map == null)
            return;
        this.pins = [];
        this.map.removeAllPins();
    }

    fitPins() {
        this.map?.fitPins();
    }

    setZoom(level: number) {
        this.map?.setZoom(level);
    }

    getZoom(): number {
        return this.map?.getZoom();
    }

    setCenter(latitude: number, longitude: number) {
        this.map?.setCenter(latitude, longitude);
    }

    getCenter(): [number, number] {
        return this.map?.getCenter();
    }

    showCircle(latitude: number, longitude: number, radius: number) {
        this.map?.showCircle(latitude, longitude, radius);
    }
    removeCircle() {
        this.map?.removeCircle();
    }

    isStyleLoaded(): boolean {
        return this.map?.isStyleLoaded() || false;
    }

    onStyleLoad(callback: () => void): void {
        if (this.isStyleLoaded()) {
            callback();
        } else {
            this.map?.onStyleLoad(callback);
        }
    }

    createRoute(routeData: any[], routeType?: any, hideMarkers?: boolean | false, routeId?: string, routeColor?: string, showProposedRoute?: boolean, disableToggleButtons?: boolean) {
        this.map?.createRoute(routeData, routeType, hideMarkers, routeId, routeColor, showProposedRoute, disableToggleButtons);
    }

    createRouteFromCoordArray(routeData: any[], routeType?: any, hideMarkers?: boolean | false, removeAllPins?: boolean | true) {
        this.map?.createRouteFromCoordArray(routeData, routeType, hideMarkers, removeAllPins);
    }

    setStopZoomLevel(isTrafficEnabled: string, routeData: any[]) {
        this.map?.setStopZoomLevel(isTrafficEnabled, routeData);
    }

    clearRoute() {
        this.map?.clearRoute();
    }

    addPinPlotListener(listener: PinPlotListener) {
        this.addEventListener(_pinPlotListenerDef, listener);
    }

    removePinPlotListener(listener: PinPlotListener) {
        this.removeEventListener(_pinPlotListenerDef, listener);
    }

    pinNeedsClickListener(pin: MapPin): boolean {
        return pin.onClick != null || pin.layoutName != null || this.pinLayout != null;
    }

    setPopupProps(popupProps: Partial<PanelProps>) {
        this.popupProps = popupProps;
    }

    setPinDetailNonModal(nonModal: boolean) {
        this._pinDetailNonModal = nonModal === true;
    }

    closePinDetails(): void {
        const overlaysToClose = [...this._pinDetailOverlays];
        this._pinDetailOverlays = [];
        overlaysToClose.forEach(overlay => Overlay.hideOverlay(overlay));
    }

    pinClicked(pin: MapPin, event: DomEvent) {
        log.debug(this, () => ["Pin clicked", pin, event]);
        if (pin.onClick != null)
            pin.onClick(new ClickEvent(this, event));
        // note this is checking for pin.layoutName === undefined so that pin.layoutName can be null
        // to allow a Map to have a pinLayout defined but a given pin can still have no layout shown when clicked
        const layoutName = pin.layoutName === undefined ? this.pinLayout : pin.layoutName;
        if (layoutName != null) {
            if (event instanceof PointerEvent) {
                if (this.popupProps == null)
                    this.popupProps = {};
                if (this.popupProps.left == null)
                    this.popupProps.left = event.clientX;
                if (this.popupProps.top == null)
                    this.popupProps.top = event.clientY;
            }
            this.showPinDetail(pin, layoutName, this.popupProps);
        }
    }

    showPinDetail(pin: MapPin, layoutName: string, popupProps: Partial<PanelProps>): void {
        const panel = new Panel({
            id: this.createInnerComponentId("panelPinDetail"),
            backgroundColor: "background2",
            borderShadow: true,
            borderRadius: 4,
            padding: 12, ...popupProps
        });
        const buttonClose = new Button({
            id: this.createInnerComponentId("buttonClosePinDetail"),
            variant: ButtonVariant.round,
            color: "subtle.light",
            imageWidth: 14,
            imageHeight: 14,
            imageName: ImageName.close
        });
        buttonClose.style.position = "absolute";
        buttonClose.style.right = "0px";
        buttonClose.style.top = "0px";
        buttonClose.zIndex = 3;
        let overlay: Overlay;
        buttonClose.addClickListener(() => Overlay.hideOverlay(overlay));
        panel.add(buttonClose);
        const layout = Layout.getLayout(layoutName, {fillRow: true, fillHeight: true});

        layout.addLayoutLoadListener(() => {
            if (layout.mainDataSource != null && (pin.data != null || pin.rows?.length > 0)) {
                if (pin.rows?.length > 0)
                    layout.mainDataSource.data = pin.rows;
                else
                    layout.mainDataSource.data = [pin.data];
                layout.mainDataSource.rowIndex = 0;
                layout.mainDataSource.displayDataInBoundComponents();
            }
            panel.add(layout);
            overlay = Overlay.showInOverlay(panel, {
                onClose: () => {
                    this._pinDetailOverlays = this._pinDetailOverlays.filter(existing => existing !== overlay);
                }
            });
            this._pinDetailOverlays.push(overlay);
            if (this._pinDetailNonModal === true) {
                // Allow map interactions to pass through outside the popup while keeping the popup interactive.
                overlay.overlayDiv.style.pointerEvents = "none";
                overlay.getOverlayContent()._element.style.pointerEvents = "auto";
            }
        });
    }

    override getPropertyDefinitions() {
        return MapPropDefinitions.getDefinitions();
    }

    override get serializationName() {
        return "map";
    }

    override get properName(): string {
        return "Map";
    }

    override getListenerDefs(): Collection<ListenerListDef> {
        return {
            ...super.getListenerDefs(),
            "pinPlot": {..._pinPlotListenerDef}
        };
    }

    public override discoverIncludedComponents(): Component[] {
        //return null here so that the components that make up the map aren't included
        return null;
    }

    private isDoNotAlwaysShowGoogleMaps(alwaysShowMap?: string) {
        const vendor = this.resolveVendor();
        // Always show maps only applies to Google Maps
        if ((vendor == null || vendor === MapVendor.GOOGLE) &&
            MapSettings.get()?.always_show_maps !== true || (StringUtil.equalsIgnoreCase(alwaysShowMap, "N"))) {
            return true;
        }
        return false;
    }

    dragEnd(pin: MapPin, event: any) {
        this.fireListeners(_dragEndListenerDef, () => new PinPlotEvent(this, pin));
    }
}

ComponentTypes.registerComponentType("map", {
    constructor: Map.prototype.constructor,
    isCompound: false,
    constructorProps: ["vendor"]
});
